import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_DELAY = 150;

class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  #currentQuery = '';
  #abortController = null;

  connectedCallback() {
    super.connectedCallback?.();
    this.resultsContainer = document.querySelector('results-list');
    this.refs.searchPageInput.addEventListener('input', this.#handleInput);
    this.refs.searchPageInput.addEventListener('keydown', this.#handleKeyDown);

    const navEntry = performance.getEntriesByType('navigation')[0];
    if (navEntry?.type === 'reload') {
      this.refs.searchPageInput.value = '';
      window.history.replaceState({}, '', (Theme.marketRoot || '') + '/search');
      this.#applyState('');
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '';
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.searchPageInput.removeEventListener('input', this.#handleInput);
    this.refs.searchPageInput.removeEventListener('keydown', this.#handleKeyDown);
    this.#abortController?.abort();
  }

  handleClearClick() {
    this.#submitEmptySearch();
  }

  #handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      return;
    }

    if (event.key === 'Escape' && this.refs.searchPageInput.value.trim() === '') {
      this.#submitEmptySearch();
    }
  };

  #applyState(state) {
    const states = ['is-too-short', 'is-no-results', 'is-loading'];
    states.forEach((s) => this.classList.toggle(s, s === state));

    if (this.resultsContainer) {
      if (state !== 'has-results') {
        this.resultsContainer.innerHTML = '';
      }
    }

    if (state === 'is-no-results') {
      const noResultsSpan = document.querySelector('.search-no-results');
      if (noResultsSpan) {
        const template = noResultsSpan.dataset.template ?? noResultsSpan.textContent;
        noResultsSpan.dataset.template = template;
        noResultsSpan.textContent = template.replace(':query', this.#currentQuery);
      }
    }
  }

  #handleInput = (() => {
    const debouncedFetch = debounce((query) => this.#fetchResults(query), DEBOUNCE_DELAY);

    return () => {
      const query = this.refs.searchPageInput.value.trim();
      const urlParams = new URLSearchParams({ q: query, type: 'product' });

      const marketRoot = Theme.marketRoot || '';
      window.history.replaceState({}, '', query.length > 0 ? `${marketRoot}/search?${urlParams}` : `${marketRoot}/search`);

      if (query.length < MIN_QUERY_LENGTH) {
        this.#abortController?.abort();
        this.#applyState(query.length > 0 ? 'is-too-short' : '');
        return;
      }

      this.#applyState('is-loading');
      debouncedFetch(query);
    };
  })();

  async #fetchResults(query) {
    if (!this.resultsContainer) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#currentQuery = query;

    const urlParams = new URLSearchParams({ q: query, type: 'product' });

    try {
      const response = await fetch(`${Theme.marketRoot || ''}/search?${urlParams}`, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      if (query !== this.#currentQuery) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newResults = doc.querySelector('results-list');

      if (newResults) {
        this.resultsContainer.replaceWith(newResults);
        this.resultsContainer = document.querySelector('results-list');
        Theme.fixMarketLinks(this.resultsContainer);

        const hasResults = this.resultsContainer.querySelector('[data-product-id]') !== null;
        this.#applyState(hasResults ? 'has-results' : 'is-no-results');
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[SearchPageInput] Fetch error:', error);
    }
  }

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;
    searchInput.focus();
    searchInput.value = '';
    this.#abortController?.abort();
    this.#applyState('');
    if (this.#isEmptyState()) return;
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    return (url.searchParams.get('q') ?? '').trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}